import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BookText, Dumbbell, Sparkles, AlertCircle, ChevronDown, ChevronUp, Settings2, ArrowLeft } from 'lucide-react'
import { aiApi } from '@/api/ai'
import { vocabularyApi } from '@/api/vocabulary'
import { useAiJobPoller, isPracticeResult } from '@/hooks/useAiJobPoller'
import { Button } from '@/components/ui/Button'
import { ExerciseSetRunner } from '@/components/ai/ExerciseSetRunner'
import { LessonGeneratingPanel } from '@/components/ai/LessonGeneratingPanel'
import { LessonExitDialog, LessonResumeDialog } from '@/components/ai/LessonSessionDialogs'
import { PracticeHistoryPanel } from '@/components/ai/PracticeHistoryPanel'
import { StoryDisplay } from '@/components/ai/StoryDisplay'
import { WordPickerPanel } from '@/components/vocabulary/WordPickerPanel'
import { cn } from '@/components/ui/cn'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { AI_CREDIT_COSTS } from '@/lib/aiCredits'
import { practiceUnitLimits } from '@/config/practiceLimits'
import { pickRandom } from '@/lib/pickRandomWords'
import { mergeWordAndPhraseRows, toPracticeUnitRef } from '@/lib/mergeVocabularyUnits'
import { useAiCredits } from '@/hooks/useAiCredits'
import { AiCreditsIndicator, AiCreditCostBadge, aiCreditsExceededMessage } from '@/components/ai/AiCreditsIndicator'
import {
  clearLessonSession,
  isResumableSession,
  loadLessonSession,
  type LessonSessionState,
} from '@/lib/lessonSessionStorage'
import { parsePracticeLesson } from '@/lib/practiceLesson'
import { loadVocabularySort, saveVocabularySort, type VocabularySortMode } from '@/lib/vocabularySortStorage'
import { findVocabWordsInText } from '@/lib/tokenizeContent'
import { useAuthStore } from '@/store/authStore'
import { useSettingsStore } from '@/store/settingsStore'
import { learningApi } from '@/api/learning'
import type { AiPracticeResult } from '@/types/ai'
import type { LessonDifficulty } from '@/types/practiceLesson'

import { useMobileViewport } from '@/hooks/useMobileViewport'

type Mode = 'STORY' | 'EXERCISES'
type GenerateState = 'idle' | 'loading' | 'done' | 'error'
type MobilePracticeStep = 'words' | 'configure'

const DIFFICULTY_OPTIONS: { value: LessonDifficulty; label: string }[] = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
]

export function AiPracticePage() {
  const { pages, practice } = useMinCopy()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const learningSessionId = searchParams.get('learningSessionId')
  const learningStepIndex = searchParams.get('stepIndex')
  const inLearningFlow = Boolean(learningSessionId)
  const userId = useAuthStore((state) => state.user?.id)
  const targetEnglishLevel = useSettingsStore((state) => state.settings?.targetEnglishLevel)
  const { max: maxPracticeUnits, defaultPick: defaultPracticePick } = useMemo(
    () => practiceUnitLimits(targetEnglishLevel),
    [targetEnglishLevel],
  )
  const { recordCharge } = useAiCredits()
  const [mode, setMode] = useState<Mode>('STORY')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [exerciseCount, setExerciseCount] = useState(7)
  const [difficulty, setDifficulty] = useState<LessonDifficulty>('medium')
  const [exerciseSetId, setExerciseSetId] = useState<string | null>(null)
  const [generateState, setGenerateState] = useState<GenerateState>('idle')
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [result, setResult] = useState<AiPracticeResult | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [addedStoryWords, setAddedStoryWords] = useState<Set<string>>(() => new Set())
  const [lessonInProgress, setLessonInProgress] = useState(false)
  const [restoredSession, setRestoredSession] = useState<LessonSessionState | null>(null)
  const [resumeSession, setResumeSession] = useState<LessonSessionState | null>(null)
  const [showResumeDialog, setShowResumeDialog] = useState(false)
  const [exitDialogOpen, setExitDialogOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const [setupExpanded, setSetupExpanded] = useState(true)
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null)
  const [generationStatus, setGenerationStatus] = useState<string | null>(null)
  const [generationElapsedSec, setGenerationElapsedSec] = useState(0)
  const isMobile = useMobileViewport(1023)
  const [mobileStep, setMobileStep] = useState<MobilePracticeStep>('words')
  const [wordSortMode, setWordSortMode] = useState<VocabularySortMode>(() => loadVocabularySort())
  const [collectionId, setCollectionId] = useState<string | undefined>()

  const wordListFilters = useMemo(
    () => ({
      size: 200,
      sortBy: wordSortMode === 'dateAdded' ? ('DATE_ADDED' as const) : ('ALPHABETICAL' as const),
      collectionId,
    }),
    [wordSortMode, collectionId],
  )

  const handleWordSortModeChange = useCallback((mode: VocabularySortMode) => {
    setWordSortMode(mode)
    saveVocabularySort(mode)
  }, [])

  const handleCollectionChange = useCallback((id: string | undefined) => {
    setCollectionId(id)
    setSelectedIds(new Set())
  }, [])

  const parsedLesson = useMemo(
    () => (result?.lesson ? parsePracticeLesson(result.lesson) : null),
    [result?.lesson],
  )
  const lessonReady = generateState === 'done' && mode === 'EXERCISES' && parsedLesson != null
  const lessonForRunner = useMemo(() => {
    if (!parsedLesson) return null
    if (parsedLesson.contextPassage?.trim()) return parsedLesson
    const passage = result?.content?.trim()
    if (!passage) return parsedLesson
    return { ...parsedLesson, contextPassage: passage }
  }, [parsedLesson, result?.content])
  const storyReady = generateState === 'done' && mode === 'STORY' && Boolean(result?.content)
  const contentReady = lessonReady || storyReady

  const { data: wordsPage, isLoading: loadingWords } = useQuery({
    queryKey: ['vocabulary', wordListFilters],
    queryFn: () => vocabularyApi.getWords(wordListFilters).then((r) => r.data),
  })

  const { data: phrasesPage } = useQuery({
    queryKey: ['vocabularyPhrases', wordListFilters],
    queryFn: () =>
      vocabularyApi
        .getPhrases({ page: 0, size: 200, search: undefined })
        .then((r) => r.data),
    enabled: !collectionId,
  })

  const { data: knownForms } = useQuery({
    queryKey: ['knownForms'],
    queryFn: () => vocabularyApi.getKnownForms().then((r) => r.data.forms),
    staleTime: 1000 * 60 * 5,
  })

  const words = useMemo(
    () => mergeWordAndPhraseRows(wordsPage?.content ?? [], phrasesPage?.content ?? []),
    [wordsPage?.content, phrasesPage?.content],
  )

  const runGenerate = useCallback(async () => {
    if (selectedIds.size === 0 || generateState === 'loading') return

    const selectedRows = words.filter((w) => selectedIds.has(w.id))
    const units = selectedRows.map(toPracticeUnitRef)

    try {
      setGenerateState('loading')
      setGenerationStartedAt(Date.now())
      setGenerationStatus(null)
      setResult(null)
      setErrorMsg(null)
      setRestoredSession(null)
      setExerciseSetId(null)
      if (userId) clearLessonSession(userId)

      const res =
        mode === 'STORY'
          ? await aiApi.generateStory({ units })
          : await aiApi.generateExercises({ units, exerciseCount, difficulty })
      await recordCharge(AI_CREDIT_COSTS.practiceGenerate)
      setActiveJobId(res.data.jobId)
    } catch (e: unknown) {
      const axiosErr = e as { response?: { status?: number; data?: { message?: string } } }
      const status = axiosErr?.response?.status
      const msg =
        status === 429
          ? aiCreditsExceededMessage(axiosErr?.response?.data?.message)
          : (axiosErr?.response?.data?.message ?? 'Could not start generation. Please try again.')
      setErrorMsg(msg)
      setGenerateState('error')
    }
  }, [selectedIds, words, generateState, mode, exerciseCount, difficulty, recordCharge, userId])

  useAiJobPoller({
    jobId: activeJobId,
    onStatus: (status) => setGenerationStatus(status),
    onDone: (response) => {
      setGenerationStartedAt(null)
      setGenerationStatus(null)
      if (isPracticeResult(response.result)) {
        setResult(response.result)
        setExerciseSetId(response.result.exerciseSetId ?? null)
        setGenerateState('done')
        setSetupExpanded(false)
      } else {
        setErrorMsg('Unexpected result type.')
        setGenerateState('error')
      }
      setActiveJobId(null)
    },
    onError: (msg) => {
      setGenerationStartedAt(null)
      setGenerationStatus(null)
      setErrorMsg(msg)
      setGenerateState('error')
      setActiveJobId(null)
    },
  })

  const toggleWord = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else if (next.size < maxPracticeUnits) {
        next.add(id)
      }
      return next
    })
  }

  const selectMany = (ids: string[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (!selected) {
        for (const id of ids) next.delete(id)
        return next
      }
      for (const id of ids) {
        if (next.size >= maxPracticeUnits) break
        next.add(id)
      }
      return next
    })
  }

  const atWordLimit = selectedIds.size >= maxPracticeUnits

  const pickRandomWords = useCallback(
    (pool: string[]) => {
      setSelectedIds(new Set(pickRandom(pool, defaultPracticePick)))
    },
    [defaultPracticePick],
  )

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size <= maxPracticeUnits) return prev
      return new Set([...prev].slice(0, maxPracticeUnits))
    })
  }, [maxPracticeUnits])

  useEffect(() => {
    if (generateState !== 'loading' || generationStartedAt == null) {
      setGenerationElapsedSec(0)
      return
    }

    const tick = () => {
      setGenerationElapsedSec(Math.floor((Date.now() - generationStartedAt) / 1000))
    }
    tick()
    const intervalId = window.setInterval(tick, 1000)
    return () => window.clearInterval(intervalId)
  }, [generateState, generationStartedAt])

  const resetToIdle = useCallback(() => {
    setGenerateState('idle')
    setResult(null)
    setErrorMsg(null)
    setActiveJobId(null)
    setRestoredSession(null)
    setExerciseSetId(null)
    setLessonInProgress(false)
    setSetupExpanded(true)
    setGenerationStartedAt(null)
    setGenerationStatus(null)
    setMobileStep('words')
  }, [])

  const requestLeaveLesson = useCallback(
    (action: () => void) => {
      if (lessonInProgress) {
        setPendingAction(() => action)
        setExitDialogOpen(true)
        return
      }
      action()
    },
    [lessonInProgress],
  )

  const handleGenerate = () => {
    requestLeaveLesson(() => {
      void runGenerate()
    })
  }

  const maybeOfferResume = useCallback(() => {
    if (!userId) return false
    const saved = loadLessonSession(userId)
    if (isResumableSession(saved, userId)) {
      setResumeSession(saved)
      setShowResumeDialog(true)
      return true
    }
    return false
  }, [userId])

  const handleModeChange = (nextMode: Mode) => {
    if (nextMode === mode) return
    requestLeaveLesson(() => {
      const hadActiveContent = generateState === 'done' || generateState === 'loading'
      setMode(nextMode)
      if (hadActiveContent) {
        resetToIdle()
      }
      if (nextMode === 'EXERCISES') {
        maybeOfferResume()
      }
    })
  }

  const handleResumeContinue = () => {
    if (!resumeSession) return
    setShowResumeDialog(false)
    setMode('EXERCISES')
    setGenerateState('done')
    setSetupExpanded(false)
    setExerciseSetId(resumeSession.exerciseSetId)
    setResult({
      content: '',
      jobType: 'GENERATE_EXERCISES',
      lesson: resumeSession.lesson,
      model: resumeSession.model,
      exerciseSetId: resumeSession.exerciseSetId,
    })
    setRestoredSession(resumeSession)
    setResumeSession(null)
  }

  const handleResumeStartNew = () => {
    if (userId) clearLessonSession(userId)
    setResumeSession(null)
    setShowResumeDialog(false)
  }

  const handleExitContinue = () => {
    setExitDialogOpen(false)
    setPendingAction(null)
  }

  const handleExitLeave = () => {
    setExitDialogOpen(false)
    const action = pendingAction
    setPendingAction(null)
    action?.()
  }

  const handleNewLesson = () => {
    if (userId) clearLessonSession(userId)
    resetToIdle()
  }

  const handleResumeFromHistory = useCallback(async (setSummary: { id: string; status: string }) => {
    try {
      const detail = await aiApi.getExerciseSet(setSummary.id).then((r) => r.data)
      setMode('EXERCISES')
      setGenerateState('done')
      setSetupExpanded(false)
      setExerciseSetId(detail.id)
      setResult({
        content: detail.lesson.contextPassage ?? '',
        jobType: 'GENERATE_EXERCISES',
        lesson: detail.lesson,
        model: detail.model ?? undefined,
        exerciseSetId: detail.id,
      })
      if (detail.status === 'IN_PROGRESS' && detail.progressJson && userId) {
        try {
          const parsed = JSON.parse(detail.progressJson) as Partial<LessonSessionState>
          setRestoredSession({
            ...parsed,
            userId,
            exerciseSetId: detail.id,
            lesson: detail.lesson,
          } as LessonSessionState)
        } catch {
          setRestoredSession(null)
        }
      } else {
        setRestoredSession(null)
      }
    } catch {
      setErrorMsg('Could not load this lesson. Please try again.')
      setGenerateState('error')
    }
  }, [userId])

  const handleLessonComplete = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['practiceExerciseSets'] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })

    if (learningSessionId && learningStepIndex != null) {
      void learningApi
        .completeStep(learningSessionId, Number(learningStepIndex))
        .then(() => navigate(`/learn/${learningSessionId}`))
    }
  }, [learningSessionId, learningStepIndex, navigate, queryClient])

  const handleLearningStepSkip = useCallback(async () => {
    if (!learningSessionId || learningStepIndex == null) return
    await learningApi.completeStep(learningSessionId, Number(learningStepIndex))
    navigate(`/learn/${learningSessionId}`)
  }, [learningSessionId, learningStepIndex, navigate])

  const selectedWords = words.filter((w) => selectedIds.has(w.id))
  const userVocabWordSet = useMemo(() => {
    const set = new Set((knownForms ?? []).map((w) => w.toLowerCase()))
    for (const lemma of addedStoryWords) {
      set.add(lemma.toLowerCase())
    }
    return set
  }, [knownForms, addedStoryWords])

  const handleStoryWordAdded = useCallback(
    (lemma: string) => {
      setAddedStoryWords((prev) => new Set(prev).add(lemma.toLowerCase()))
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
      void queryClient.invalidateQueries({ queryKey: ['knownForms'] })
      void queryClient.refetchQueries({ queryKey: ['vocabulary', { size: 200 }] })
    },
    [queryClient],
  )

  useEffect(() => {
    if (generateState !== 'done' || mode !== 'STORY' || !result?.content) return
    const encountered = findVocabWordsInText(result.content, userVocabWordSet)
    if (encountered.length === 0) return
    void aiApi.recordStoryEncounters(encountered)
  }, [generateState, mode, result?.content, userVocabWordSet])

  useEffect(() => {
    maybeOfferResume()
  }, [maybeOfferResume])

  useEffect(() => {
    if (!isMobile) return
    document.querySelector('main')?.scrollTo(0, 0)
  }, [isMobile, mobileStep, generateState])

  const immersiveLessonOpen =
    isMobile && contentReady && generateState === 'done' && Boolean(result)

  useEffect(() => {
    if (!immersiveLessonOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [immersiveLessonOpen])

  useEffect(() => {
    if (contentReady) {
      setSetupExpanded(false)
    }
  }, [contentReady])

  const learningFlowBanner = inLearningFlow ? (
    <div
      className="flex shrink-0 items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
      data-testid="practice-learning-flow-banner"
    >
      <p className="text-sm text-muted-foreground">
        Guided session — finish a lesson to continue, or mark this step done.
      </p>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={() => void handleLearningStepSkip()}
        data-testid="practice-learning-mark-done"
      >
        Mark done
      </Button>
    </div>
  ) : null

  const articleTipBanner =
    !inLearningFlow && !contentReady ? (
      <div
        className="flex shrink-0 flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between"
        data-testid="practice-article-tip"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{practice.articleTipTitle}</p>
          <p className="mt-1 text-sm text-muted-foreground">{practice.articleTipBody}</p>
        </div>
        <Link to="/reading" className="shrink-0">
          <Button variant="outline" size="sm" className="w-full sm:w-auto">
            <BookText className="mr-1.5 h-4 w-4" />
            {practice.articleTipCta}
          </Button>
        </Link>
      </div>
    ) : null

  const setupPanel = (options?: { showGenerateButton?: boolean }) => (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {mode === 'EXERCISES' && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Exercises</span>
                <select
                  value={exerciseCount}
                  onChange={(e) => setExerciseCount(Number(e.target.value))}
                  className="rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {[3, 5, 7, 10].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <fieldset className="flex items-center gap-2 text-sm">
                <legend className="sr-only">Difficulty</legend>
                <span className="text-muted-foreground">Difficulty</span>
                <div className="flex rounded-lg border bg-muted/50 p-0.5">
                  {DIFFICULTY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDifficulty(opt.value)}
                      className={cn(
                        'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                        difficulty === opt.value
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </fieldset>
            </>
          )}

          {options?.showGenerateButton !== false ? (
            <Button
              className="ml-auto w-auto px-5"
              disabled={selectedIds.size === 0 || generateState === 'loading'}
              loading={generateState === 'loading'}
              onClick={handleGenerate}
            >
              {generateState !== 'loading' && <Sparkles className="h-4 w-4" />}
              {mode === 'STORY' ? practice.generateStory : contentReady ? practice.newLesson : practice.generateLesson}
              {generateState !== 'loading' ? (
                <AiCreditCostBadge cost={AI_CREDIT_COSTS.practiceGenerate} className="ml-1 bg-primary-foreground/15 text-primary-foreground" />
              ) : null}
            </Button>
          ) : null}
        </div>

        {selectedIds.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <span
              className={cn(
                'text-xs font-medium',
                atWordLimit ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
              )}
            >
              {formatMessage(practice.wordLimit, { selected: selectedIds.size, max: maxPracticeUnits })}
            </span>
            <div className="flex flex-wrap gap-1">
              {selectedWords.slice(0, 12).map((w) => (
                <span
                  key={w.id}
                  className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                >
                  {w.word}
                </span>
              ))}
              {selectedWords.length > 12 && (
                <span className="self-center text-xs text-muted-foreground">
                  +{selectedWords.length - 12} more
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{practice.selectToStart}</p>
        )}
      </div>

      {generateState === 'error' && (
        <div className="mt-4 space-y-3 border-t pt-4">
          <div className="flex items-start gap-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
          <Button
            className="h-8 w-auto bg-secondary px-4 text-xs text-secondary-foreground hover:bg-secondary/80"
            onClick={resetToIdle}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  )

  const wordPickerFooter = (
    <div className="flex shrink-0 items-center justify-between border-t px-4 py-3">
      <div className="flex items-center gap-3">
        {words.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const ids = words.map((w) => w.id)
              if (selectedIds.size >= maxPracticeUnits) {
                selectMany(ids, false)
              } else {
                pickRandomWords(ids)
              }
            }}
            className="text-xs font-medium text-primary hover:underline"
            data-testid="practice-select-all"
          >
            {selectedIds.size >= maxPracticeUnits
              ? 'Deselect all'
              : formatMessage(practice.pickRandom, { count: defaultPracticePick })}
          </button>
        )}
        {selectedIds.size > 0 ? (
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear selection
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">{words.length} items</span>
        )}
      </div>
    </div>
  )

  const dialogs = (
    <>
      {showResumeDialog && resumeSession ? (
        <LessonResumeDialog
          lessonTitle={resumeSession.lesson.title}
          exerciseLabel={`Exercise ${resumeSession.currentIndex + 1} / ${resumeSession.stepExerciseIds.length}`}
          onContinue={handleResumeContinue}
          onStartNew={handleResumeStartNew}
        />
      ) : null}

      <LessonExitDialog
        open={exitDialogOpen}
        onContinue={handleExitContinue}
        onLeave={handleExitLeave}
      />
    </>
  )

  if (isMobile) {
    if (generateState === 'loading') {
      return (
        <div className="flex min-h-[calc(100dvh-5rem)] flex-col" data-testid="practice-mobile-loading">
          <LessonGeneratingPanel
            mode={mode}
            elapsedSeconds={generationElapsedSec}
            status={generationStatus}
            exerciseCount={exerciseCount}
            wordCount={selectedIds.size}
          />
          {dialogs}
        </div>
      )
    }

    if (contentReady && generateState === 'done' && result) {
      return (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-background"
          data-testid="practice-mobile-runner"
        >
          {mode === 'STORY' ? (
            <>
              <div className="flex shrink-0 items-center gap-2 border-b px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))]">
                <button
                  type="button"
                  onClick={() => requestLeaveLesson(resetToIdle)}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Back to setup"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">Your story</p>
                  <p className="text-xs text-muted-foreground">{selectedIds.size} items</p>
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <StoryDisplay
                  content={result.content}
                  targetWords={selectedWords.map((w) => w.word)}
                  userVocabWords={userVocabWordSet}
                  onWordAdded={handleStoryWordAdded}
                />
              </div>
            </>
          ) : lessonForRunner && userId ? (
            <ExerciseSetRunner
              lesson={lessonForRunner}
              model={result.model}
              userId={userId}
              exerciseSetId={exerciseSetId}
              initialSession={restoredSession}
              layout="mobile"
              onBack={() => requestLeaveLesson(resetToIdle)}
              onInProgressChange={setLessonInProgress}
              onNewLesson={handleNewLesson}
              onLessonComplete={handleLessonComplete}
              className="min-h-0 flex-1"
            />
          ) : (
            <p className="p-4 text-sm text-destructive">Could not load this lesson. Please generate again.</p>
          )}
          {dialogs}
        </div>
      )
    }

    if (mobileStep === 'words') {
      const selectableWordIds = words.map((w) => w.id)

      return (
        <div
          className="flex h-[calc(100dvh-5.5rem)] flex-col overflow-hidden rounded-xl border bg-card"
          data-testid="practice-mobile-words"
        >
          <WordPickerPanel
            words={words}
            selectedIds={selectedIds}
            onToggle={toggleWord}
            onSelectMany={selectMany}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            loading={loadingWords}
            separateMastered
            maxSelection={maxPracticeUnits}
            randomPickCount={defaultPracticePick}
            onPickRandom={pickRandomWords}
            hideQuickPick
            showSortToggle
            sortMode={wordSortMode}
            onSortModeChange={handleWordSortModeChange}
            showCollectionFilter
            collectionId={collectionId}
            onCollectionChange={handleCollectionChange}
            className="min-h-0 flex-1 overflow-hidden"
          />

          <div className="shrink-0 border-t bg-card/95 px-3 py-2 backdrop-blur-sm pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            <div className="mb-2 flex items-center gap-3">
              {words.length > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (selectedIds.size >= maxPracticeUnits) {
                      selectMany(selectableWordIds, false)
                    } else {
                      pickRandomWords(selectableWordIds)
                    }
                  }}
                  className="text-xs font-medium text-primary hover:underline"
                  data-testid="word-picker-select-all"
                >
                  {selectedIds.size >= maxPracticeUnits
                    ? 'Deselect all'
                    : formatMessage(practice.pickRandom, { count: defaultPracticePick })}
                </button>
              ) : null}
              {selectedIds.size > 0 ? (
                <button
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              ) : null}
              <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                {formatMessage(practice.wordLimit, { selected: selectedIds.size, max: maxPracticeUnits })}
              </span>
            </div>
            <Button
              className="min-h-11 w-full"
              disabled={selectedIds.size === 0}
              onClick={() => setMobileStep('configure')}
              data-testid="practice-mobile-continue"
            >
              Continue
            </Button>
          </div>
          {dialogs}
        </div>
      )
    }

    return (
      <div
        className="flex h-[calc(100dvh-5.5rem)] flex-col overflow-hidden"
        data-testid="practice-mobile-configure"
      >
        {learningFlowBanner ? <div className="shrink-0 px-1 pt-2">{learningFlowBanner}</div> : null}
        <div className="shrink-0 flex items-center gap-2 border-b px-1 py-2">
          <button
            type="button"
            onClick={() => setMobileStep('words')}
            className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Back to word selection"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-base font-bold tracking-tight">Configure practice</h1>
            <p className="text-xs text-muted-foreground">
              {formatMessage(practice.wordLimit, { selected: selectedIds.size, max: maxPracticeUnits })}
            </p>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-1 pb-2">
          <div className="flex flex-col gap-2">
            <ModeButton
              active={mode === 'STORY'}
              icon={<BookText className="h-4 w-4" />}
              label={practice.generateStory}
              description={practice.storyDesc}
              onClick={() => handleModeChange('STORY')}
              className="w-full"
            />
            <ModeButton
              active={mode === 'EXERCISES'}
              icon={<Dumbbell className="h-4 w-4" />}
              label={practice.generateLesson}
              description={practice.lessonDesc}
              onClick={() => handleModeChange('EXERCISES')}
              className="w-full"
            />
          </div>

          {setupPanel({ showGenerateButton: false })}

          {!lessonInProgress && mode !== 'STORY' ? (
            <PracticeHistoryPanel onResume={handleResumeFromHistory} />
          ) : null}
        </div>

        <div className="shrink-0 border-t bg-background/95 px-3 py-2 backdrop-blur-sm pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <Button
            className="min-h-11 w-full"
            disabled={selectedIds.size === 0 || generateState === 'loading'}
            loading={generateState === 'loading'}
            onClick={handleGenerate}
            data-testid="practice-mobile-generate"
          >
            {generateState !== 'loading' && <Sparkles className="h-4 w-4" />}
            {mode === 'STORY' ? practice.generateStory : practice.generateLesson}
            {generateState !== 'loading' ? (
              <AiCreditCostBadge cost={AI_CREDIT_COSTS.practiceGenerate} className="ml-1 bg-primary-foreground/15 text-primary-foreground" />
            ) : null}
          </Button>
        </div>
        {dialogs}
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100dvh-1.5rem)] gap-6 overflow-hidden sm:h-[calc(100dvh-2rem)]">
      <aside className="flex h-full w-80 shrink-0 flex-col overflow-hidden rounded-xl border bg-card">
        <div className="shrink-0 border-b p-4">
          <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-primary" />
            {practice.pickWords}
            {selectedIds.size > 0 && (
              <span
                className={cn(
                  'ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                  atWordLimit
                    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                    : 'bg-primary text-primary-foreground'
                )}
              >
                {formatMessage(practice.wordLimit, { selected: selectedIds.size, max: maxPracticeUnits })}
              </span>
            )}
          </h2>
          <p className="text-xs text-muted-foreground">{formatMessage(practice.wordLimitHint, { max: maxPracticeUnits })}</p>
          {atWordLimit ? (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              {formatMessage(practice.wordLimitReached, { max: maxPracticeUnits })}
            </p>
          ) : null}
          <AiCreditsIndicator compact className="mt-2" />
        </div>

        <WordPickerPanel
          words={words}
          selectedIds={selectedIds}
          onToggle={toggleWord}
          onSelectMany={selectMany}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          loading={loadingWords}
          separateMastered
          maxSelection={maxPracticeUnits}
          randomPickCount={defaultPracticePick}
          onPickRandom={pickRandomWords}
          hideQuickPick
          showSortToggle
          sortMode={wordSortMode}
          onSortModeChange={handleWordSortModeChange}
          showCollectionFilter
          collectionId={collectionId}
          onCollectionChange={handleCollectionChange}
          className="min-h-0 flex-1 overflow-hidden"
        />

        {wordPickerFooter}
      </aside>

      <div
        className={cn(
          'flex h-full min-h-0 flex-1 flex-col gap-3',
          contentReady ? 'overflow-hidden' : 'overflow-y-auto',
        )}
      >
        {learningFlowBanner}
        {articleTipBanner}
        {!contentReady ? (
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{pages.practice}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {pages.practiceSubtitle}
            </p>
          </div>
            ) : null}

        {generateState === 'loading' ? (
          <LessonGeneratingPanel
            mode={mode}
            elapsedSeconds={generationElapsedSec}
            status={generationStatus}
            exerciseCount={exerciseCount}
            wordCount={selectedIds.size}
          />
        ) : null}

        {!contentReady || setupExpanded ? (
          <>
            {!contentReady ? (
              <div className="flex gap-2">
                <ModeButton
                  active={mode === 'STORY'}
                  icon={<BookText className="h-4 w-4" />}
                  label={practice.generateStory}
                  description={practice.storyDesc}
                  onClick={() => handleModeChange('STORY')}
                />
                <ModeButton
                  active={mode === 'EXERCISES'}
                  icon={<Dumbbell className="h-4 w-4" />}
                  label={practice.generateLesson}
                  description={practice.lessonDesc}
                  onClick={() => handleModeChange('EXERCISES')}
                />
              </div>
            ) : null}

            {contentReady && setupExpanded ? (
              <>
                <div className="flex gap-2">
                  <ModeButton
                    active={mode === 'STORY'}
                    icon={<BookText className="h-4 w-4" />}
                    label={practice.generateStory}
                    description={practice.storyDesc}
                    onClick={() => handleModeChange('STORY')}
                  />
                  <ModeButton
                    active={mode === 'EXERCISES'}
                    icon={<Dumbbell className="h-4 w-4" />}
                    label={practice.generateLesson}
                    description={practice.lessonDesc}
                    onClick={() => handleModeChange('EXERCISES')}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Practice settings</p>
                  <button
                    type="button"
                    onClick={() => setSetupExpanded(false)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Hide
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                </div>
              </>
            ) : null}

            {!lessonReady || setupExpanded ? setupPanel() : null}

            {!lessonInProgress && mode !== 'STORY' && (!contentReady || setupExpanded) ? (
              <PracticeHistoryPanel onResume={handleResumeFromHistory} />
            ) : null}
          </>
        ) : null}

        {contentReady && !setupExpanded ? (
          <div className="flex shrink-0 items-center justify-between rounded-lg border bg-card px-3 py-2">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              {mode === 'STORY' ? (
                <BookText className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <Dumbbell className="h-4 w-4 shrink-0 text-primary" />
              )}
              <span className="truncate font-medium">
                {lessonReady ? lessonForRunner!.title : 'Your story'}
              </span>
              <span className="shrink-0 text-muted-foreground">· {selectedIds.size} items</span>
            </div>
            <button
              type="button"
              onClick={() => setSetupExpanded(true)}
              className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              data-testid="practice-show-settings"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Settings
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}

        {generateState === 'done' && result ? (
          <div className={cn('flex min-h-0 flex-1 flex-col', contentReady && 'overflow-hidden')}>
            {mode === 'STORY' ? (
              <div className="flex min-h-0 flex-1 flex-col space-y-3 overflow-y-auto">
                {!setupExpanded ? (
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Your Story</h3>
                    <div className="flex items-center gap-3">
                      {result.model && (
                        <span className="text-xs text-muted-foreground">{result.model}</span>
                      )}
                      <button
                        onClick={resetToIdle}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Generate again
                      </button>
                    </div>
                  </div>
                ) : null}
                <StoryDisplay
                  content={result.content}
                  targetWords={selectedWords.map((w) => w.word)}
                  userVocabWords={userVocabWordSet}
                  onWordAdded={handleStoryWordAdded}
                />
              </div>
            ) : lessonForRunner && userId ? (
              <ExerciseSetRunner
                lesson={lessonForRunner}
                model={result.model}
                userId={userId}
                exerciseSetId={exerciseSetId}
                initialSession={restoredSession}
                onInProgressChange={setLessonInProgress}
                onNewLesson={handleNewLesson}
                onLessonComplete={handleLessonComplete}
                className="min-h-0 flex-1"
              />
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Your Lesson</h3>
                  <button
                    onClick={resetToIdle}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Try again
                  </button>
                </div>
                <p className="text-sm text-destructive">
                  Could not load this lesson. Please generate again.
                </p>
              </div>
            )}
          </div>
        ) : !contentReady && generateState === 'idle' ? null : null}
      </div>

      {dialogs}
    </div>
  )
}

function ModeButton({
  active,
  icon,
  label,
  description,
  onClick,
  className,
}: {
  active: boolean
  icon: ReactNode
  label: string
  description: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-start gap-3 rounded-xl border p-4 text-left transition-colors',
        active
          ? 'border-primary bg-primary/5 text-foreground'
          : 'border-border hover:border-primary/40 hover:bg-accent/50',
        className ?? 'flex-1',
      )}
    >
      <div
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
        )}
      >
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
    </button>
  )
}
